export const ALL_AVAILABLE_MODULES = [
  "home",
  "restaurant-info",
  "pos",
  "kitchen",
  "clients",
  "events",
  "support",
  "qrcode",
  "admin-history",
];

export const MODULE_TO_MENU_KEYS = {
  home: ["home"],
  "restaurant-info": ["restaurant-info"],
  pos: [
    "pos",
    "pos-orders",
    "pos-orders-board",
    "pos-reservations",
    "arch-table-reservations",
  ],
  kitchen: [
    "kitchen",
    "kitchen-menu",
    "kitchen-categories",
    "kitchen-subcategories",
    "kitchen-products",
    "kitchen-choices",
    "kitchen-items",
    "arch-menu",
    "arch-categories",
    "arch-subcategories",
    "arch-products",
    "arch-items",
    "arch-choices",
  ],
  clients: ["clients", "arch-clients", "arch-plan-floor-setups"],
  events: ["events", "arch-events", "arch-event-reservations"],
  support: [
    "support",
    "support-my-requests",
    "support-claims",
    "support-client-feedback",
    "support-lost-found",
    "arch-support-requests",
    "arch-claims",
    "arch-client-feedback",
    "arch-lost-found",
  ],
  qrcode: ["qrcode"],
  "admin-history": ["admin-history", "arch-admin-history"],
};

export const ROUTE_MODULE_MAP = {
  // Home menu is gated via MODULE_TO_MENU_KEYS; /dashboard stays a safe landing.

  // Restaurant information
  "app-profile": "restaurant-info",

  // POS
  orders: "pos",
  "orders-board": "pos",
  reservations: "pos",
  "Order-id": "pos",
  calls: "pos",
  "archived-table-reservations": "pos",

  // Kitchen
  menu: "kitchen",
  "add-menu": "kitchen",
  "edit-menu": "kitchen",
  "manage-menu": "kitchen",
  "menu-details": "kitchen",
  categories: "kitchen",
  "add-category": "kitchen",
  "category-details": "kitchen",
  "edit-category": "kitchen",
  subcategoriesList: "kitchen",
  subcategories: "kitchen",
  "add-subcategory": "kitchen",
  "subcategory-details": "kitchen",
  "edit-subcategory": "kitchen",
  products: "kitchen",
  "create-product": "kitchen",
  "add-product": "kitchen",
  "add-new-product": "kitchen",
  "product-sheet": "kitchen",
  "edit-product": "kitchen",
  choices: "kitchen",
  "add-choice": "kitchen",
  "choice-details": "kitchen",
  "edit-choice": "kitchen",
  items: "kitchen",
  "add-item": "kitchen",
  "item-details": "kitchen",
  "edit-item": "kitchen",
  "menu-history": "kitchen",
  "products-history": "kitchen",
  "categories-history": "kitchen",
  "items-history": "kitchen",
  "choices-history": "kitchen",
  "subcategoriesList-history": "kitchen",
  "subcategories-history": "kitchen",
  "start-now": "kitchen",

  // Clients
  clients: "clients",
  "archived-clients": "clients",
  "archived-plan-floor-setups": "clients",
  "general-customers": "clients",
  "google-users": "clients",

  // Events
  events: "events",
  "archived-events": "events",
  "archived-event-reservations": "events",

  // Support
  support: "support",
  "my-support-requests": "support",
  "archived-support-requests": "support",
  "client-reclamations": "support",
  "archived-client-reclamations": "support",
  "client-feedback": "support",
  "archived-client-feedback": "support",
  "lost-found": "support",
  "archived-lost-found": "support",

  // QR Code
  "app-qrcode": "qrcode",

  // Admin History
  "admin-history": "admin-history",
  "archived-admin-history": "admin-history",

  // Owner Only
  "restaurant-users": "__owner_only__",
  "add-restaurant-user": "__owner_only__",
};

export function getStoredIsOwner() {
  try {
    const raw = localStorage.getItem("isOwner");
    return raw ? JSON.parse(raw) === true : false;
  } catch {
    return false;
  }
}

export function getStoredUserPermissions() {
  const isOwner = getStoredIsOwner();
  if (isOwner) return ALL_AVAILABLE_MODULES;

  try {
    const raw = localStorage.getItem("userPermissions");
    if (!raw) return ALL_AVAILABLE_MODULES;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : ALL_AVAILABLE_MODULES;
  } catch {
    return ALL_AVAILABLE_MODULES;
  }
}

export function computeDisabledMenuKeysForUser(userOrPermissions, isOwner) {
  // Primary owner has access to everything
  if (isOwner === true) {
    return [];
  }

  let perms = [];
  if (Array.isArray(userOrPermissions)) {
    perms = userOrPermissions;
  } else if (userOrPermissions && typeof userOrPermissions === "object") {
    if (userOrPermissions.isOwner) return [];
    if (Array.isArray(userOrPermissions.permissions)) {
      perms = userOrPermissions.permissions;
    }
  }

  const disabled = new Set();

  // Secondary admins cannot manage restaurant users
  disabled.add("restaurant-users");

  // Disable menu keys for unpermitted modules
  for (const [moduleKey, keys] of Object.entries(MODULE_TO_MENU_KEYS)) {
    if (!perms.includes(moduleKey)) {
      keys.forEach((k) => disabled.add(k));
    }
  }

  // Hide Restaurant group if none of its children remain
  const restaurantChildren = ["home", "restaurant-info", "restaurant-users", "qrcode"];
  const hasRestaurantChild = restaurantChildren.some(
    (key) => !disabled.has(key)
  );
  if (!hasRestaurantChild) {
    disabled.add("restaurant");
  }

  // Check if any archive module is available
  const archiveModules = [
    "pos",
    "kitchen",
    "clients",
    "events",
    "support",
    "admin-history",
  ];
  const hasArchiveAccess = archiveModules.some((m) => perms.includes(m));
  if (!hasArchiveAccess) {
    disabled.add("archives");
  }

  return Array.from(disabled);
}

export function getRequiredModuleForPath(pathname) {
  const clean = String(pathname || "")
    .replace(/^\/+/, "")
    .replace(/\/+$/, "");
  const firstSegment = clean.split("/")[0] || "";

  return ROUTE_MODULE_MAP[firstSegment] || null;
}

export function canAccessPath(pathname, user) {
  const required = getRequiredModuleForPath(pathname);
  if (!required) return true; // Unrestricted path (e.g. dashboard, profile, etc.)

  const isOwner =
    user?.isOwner !== undefined
      ? Boolean(user.isOwner)
      : getStoredIsOwner();

  if (isOwner) return true;

  // Owner-only route
  if (required === "__owner_only__") {
    return false;
  }

  // Check user permissions
  const permissions =
    user?.permissions !== undefined
      ? user.permissions
      : getStoredUserPermissions();

  if (!Array.isArray(permissions)) {
    return true; // fallback
  }

  return permissions.includes(required);
}
